import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import AmbientLiveMap from "@/components/AmbientLiveMap";
import NewLiveTicker from "@/components/LiveTicker";
import { openAndroidBetaModal } from "@/lib/androidBeta";

import StickyWalkthrough from "@/components/StickyWalkthrough";
import {
  ArrowRight,
  MapPin,
  Bell,
  Users,
  Shield,
  Sparkles,
  Apple,
  Play,
  Star,
  Gift,
  Award,
  Trophy,
  ChevronDown,
  Instagram,
  Twitter,
  Youtube,
  Music2,
  Heart,
  Compass,
  Calendar,
  LifeBuoy,
  Coffee,
  Store,
  Zap,
  Building2,
  Volume2,
  VolumeX,
  Copy,
  Share2,
  Check,
  Coins,
  Menu,
  X,
  BadgeCheck,
  SlidersHorizontal,
} from "lucide-react";

import PhoneMockup from "@/components/PhoneMockup";
import RealPhone from "@/components/RealPhone";
import ContactModal from "@/components/ContactModal";
import CountUp from "@/components/CountUp";
import { LiveStat, joinedInLastHour } from "@/components/LiveActiveMembers";
import ConciergeChat from "@/components/ConciergeChat";
import { SupportChatTrigger } from "@/components/SupportChat";
import ActivitiesNearYou from "@/components/ActivitiesNearYou";
import { IMG, STORE } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- REFERRAL HELPERS ---------------- */
const REF_STORAGE_KEY = "fotr_ref_code";

function getRefFromUrl() {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  const code = (p.get("ref") || "").trim().toUpperCase();
  return code || null;
}

function readStoredRef() {
  try {
    return (localStorage.getItem(REF_STORAGE_KEY) || "").trim().toUpperCase() || null;
  } catch {
    return null;
  }
}

function persistRef(code) {
  if (!code) return;
  try {
    localStorage.setItem(REF_STORAGE_KEY, code);
  } catch {
    /* localStorage unavailable */
  }
}

function buildShareUrl(code) {
  if (typeof window === "undefined") return `https://funontherun.co/?ref=${code}`;
  const origin = window.location.hostname.includes("funontherun.co")
    ? "https://funontherun.co"
    : window.location.origin;
  return `${origin}/?ref=${code}`;
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function Landing() {
  return (
    <div className="relative overflow-x-hidden">
      <Nav />
      <InviterBanner />
      <Hero />
      <AnimatedBrandBanner />
      <LiveTicker />
      <Mavericks />
      <VendorsTeaser />
      <CampusPartners />
      <BrandPillars />
      <AppShowcase />
      <CityDensity />
      <ActivitiesNearYou />
      <SocialProof />
      <ConciergeSection />
      <Founder />
      <Ambassadors />
      <Investors />
      <Referrals />
      <Leaderboard />
      <VideoContent />
      <Safety />
      <Inclusive />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ---------------- NAV ---------------- */
export function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Logo "breath" on first session load — letter-spacing expands once
  // (Ruben-Stom-inspired luxe opener). Only fires once per browser session.
  const [shouldBreathe] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("fotr_logo_breathed")) return false;
      sessionStorage.setItem("fotr_logo_breathed", "1");
      return true;
    } catch { return false; }
  });

  // Match the lazy() imports in App.js so hovering a nav link warms up that route's chunk.
  // Webpack dedupes by module path, so the chunk loaded here is the SAME one App.js's
  // Suspense will use when the user actually clicks.
  const PREFETCH = {
    "/app":       () => import("@/pages/AppPage"),
    "/community": () => import("@/pages/Community"),
    "/concierge": () => import("@/pages/Concierge"),
    "/story":     () => import("@/pages/Story"),
    "/safety":    () => import("@/pages/SafetyPage"),
    "/vendors":   () => import("@/pages/Vendors"),
    "/invest":    () => import("@/pages/Invest"),
    "/join":      () => import("@/pages/Join"),
  };
  const prefetched = React.useRef(new Set());
  const handlePrefetch = (to) => {
    if (prefetched.current.has(to)) return;
    const loader = PREFETCH[to];
    if (loader) {
      prefetched.current.add(to);
      loader().catch(() => prefetched.current.delete(to));
    }
  };

  const links = [
    { to: "/", label: "Home", sub: "Start here", testid: "nav-link-home" },
    { to: "/app", label: "Fun in my City", sub: "What's on tonight", testid: "nav-link-app" },
    { to: "/community", label: "Community", sub: "Meet members", testid: "nav-link-community" },
    { to: "/concierge", label: "AI Concierge", sub: "Tell us your vibe", testid: "nav-link-concierge" },
    { to: "/story", label: "Story", sub: "Why we built it", testid: "nav-link-story" },
    { to: "/safety", label: "Safety", sub: "Trust & guidelines", testid: "nav-link-safety" },
    { to: "/vendors", label: "Vendors", sub: "Partner with us", testid: "nav-link-vendors" },
    { to: "/invest", label: "Invest", sub: "Back the mission", testid: "nav-link-invest" },
  ];

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-line">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity" data-testid="nav-logo">
          <motion.img
            src={IMG.logoMark}
            alt="Fun On The Run Plus One"
            className="w-9 h-9 rounded-xl object-contain bg-ink p-1"
            initial={shouldBreathe ? { scale: 0.6, opacity: 0, rotate: -8 } : false}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 1.0, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.span
            className="font-display font-black tracking-tight text-lg hidden sm:inline"
            initial={shouldBreathe ? { letterSpacing: "0.45em", opacity: 0, x: 6 } : false}
            animate={{ letterSpacing: "-0.025em", opacity: 1, x: 0 }}
            transition={{ duration: 1.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            fun on the run<span className="text-primary"> plus one</span>
          </motion.span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium" data-testid="nav-desktop">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              onMouseEnter={() => handlePrefetch(l.to)}
              onFocus={() => handlePrefetch(l.to)}
              onTouchStart={() => handlePrefetch(l.to)}
              className={`relative transition-colors hover:text-primary ${isActive(l.to) ? "text-ink" : "text-muted"}`}
            >
              {l.label}
              {isActive(l.to) && (
                <span className="absolute -bottom-[22px] left-0 right-0 h-[3px] bg-secondary rounded-full" />
              )}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/join"
            onMouseEnter={() => handlePrefetch("/join")}
            onFocus={() => handlePrefetch("/join")}
            onTouchStart={() => handlePrefetch("/join")}
            className="hidden sm:inline-flex bg-ink text-white px-5 py-2.5 rounded-full text-sm font-semibold btn-shimmer hover:bg-primary transition-colors"
            data-testid="nav-cta-signup"
          >
            Sign up for beta
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full border border-line text-ink hover:bg-ink hover:text-white transition-colors"
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            <span className="text-xs font-bold uppercase tracking-[0.15em]">
              {mobileOpen ? "Close" : "Menu"}
            </span>
          </button>
        </div>
      </div>
      {/* Mobile drawer with staggered children entrance */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="lg:hidden border-t border-line bg-background/95 backdrop-blur-xl overflow-hidden"
            data-testid="nav-mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.nav
              className="px-6 py-5 flex flex-col gap-1"
              initial="closed"
              animate="open"
              exit="closed"
              variants={{
                open:   { transition: { staggerChildren: 0.05, delayChildren: 0.08 } },
                closed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
              }}
            >
              <motion.div
                variants={{
                  open:   { opacity: 1, x: 0 },
                  closed: { opacity: 0, x: -18 },
                }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="px-3 pb-3 mb-1 border-b border-line"
                data-testid="mobile-drawer-heading"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted">Menu</div>
                <div className="text-xs text-muted/80 mt-1">Tap a section to jump in</div>
              </motion.div>
              {links.map((l) => (
                <motion.div
                  key={l.to}
                  variants={{
                    open:   { opacity: 1, x: 0 },
                    closed: { opacity: 0, x: -18 },
                  }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onTouchStart={() => handlePrefetch(l.to)}
                    onClick={() => setMobileOpen(false)}
                    className={`flex flex-col px-3 py-3 rounded-xl transition-colors ${isActive(l.to) ? "bg-ink text-white" : "text-ink hover:bg-line"}`}
                  >
                    <span className="text-base font-semibold leading-tight">{l.label}</span>
                    {l.sub && (
                      <span className={`text-xs font-normal mt-0.5 ${isActive(l.to) ? "text-white/70" : "text-muted"}`}>
                        {l.sub}
                      </span>
                    )}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                variants={{
                  open:   { opacity: 1, x: 0 },
                  closed: { opacity: 0, x: -18 },
                }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to="/join"
                  onClick={() => setMobileOpen(false)}
                  className="block mt-3 bg-primary text-white px-4 py-3 rounded-xl text-center font-bold"
                  data-testid="mobile-nav-cta-signup"
                >
                  Sign up for beta
                </Link>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ---------------- HERO ---------------- */
export function Hero() {
  // Defensive autoplay — some browsers/CDN conditions ignore the autoplay
  // attribute (esp. on production where the video is range-streamed through
  // a CDN). Calling .play() explicitly after mount is the standard fix.
  const bgVideoRef = React.useRef(null);
  React.useEffect(() => {
    const v = bgVideoRef.current;
    if (!v) return;
    v.muted = true;          // re-assert before play (Safari quirk)
    v.playsInline = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // If autoplay was deferred until the document gets focus / user interaction,
    // retry on the first interaction.
    const onInteract = () => {
      tryPlay();
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      document.removeEventListener("visibilitychange", onVisible);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tryPlay();
    };
    window.addEventListener("click", onInteract, { once: true });
    window.addEventListener("touchstart", onInteract, { once: true });
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <>
      <section
        id="top"
        className="relative isolate overflow-hidden min-h-[88vh] md:min-h-[92vh] flex items-center px-6 md:px-12 py-20 md:py-28"
        data-testid="hero-section"
      >
        {/* Background looping cinematic video — fades in over 1.5s on load */}
        {/* Background looping cinematic video — wrapped in motion.div so framer doesn't interfere with the <video> element itself */}
        <motion.div
          className="absolute inset-0 -z-20"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
        >
          <video
            ref={bgVideoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={IMG.heroBgPoster}
            src={IMG.heroBgVideo}
            onCanPlay={(e) => {
              if (e.currentTarget.paused) e.currentTarget.play().catch(() => {});
            }}
            className="w-full h-full object-cover"
            aria-hidden="true"
            data-testid="hero-bg-video"
          />
        </motion.div>
        {/* Dark gradient overlay (~57% avg) for text legibility — fades in slightly behind video */}
        <motion.div
          className="absolute inset-0 -z-10 bg-gradient-to-b from-black/62 via-black/52 to-black/72"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.8, ease: "easeOut", delay: 0.1 }}
        />
        {/* Ambient "live map" — soft radial pulses + drifting connection lines.
            Sits above the dark overlay but below content. 10% opacity max. */}
        <AmbientLiveMap />

        <div className="relative max-w-5xl mx-auto w-full text-center text-white">
          {/* Cinematic staggered fade-in (~3s total) */}
          <motion.span
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-white/85 mb-6"
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            <span className="w-2 h-2 rounded-full bg-primary pulse-dot" />
            Live across the US · Pilots in Dallas · Fort Worth · Austin · New Orleans
          </motion.span>
          <motion.h1
            className="font-display font-black tracking-tighter leading-[0.92] text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            Meet real people in{" "}
            <span
              className="text-primary"
              style={{
                textShadow:
                  "0 0.08em 0 rgba(255, 195, 0, 0.55), 0 0.16em 14px rgba(255, 195, 0, 0.35)",
              }}
            >
              real time
            </span>, doing things you love.
          </motion.h1>
          <motion.p
            className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.6, ease: "easeOut" }}
          >
            Get instant access to individuals and groups who share your interests. Build genuine connections through the things you love, without needing an existing friend group and no swiping.
          </motion.p>
          <motion.p
            className="mt-3 text-sm md:text-base uppercase tracking-[0.2em] font-bold text-secondary"
            style={{ textShadow: "0 1px 10px rgba(0,0,0,0.5)" }}
            data-testid="hero-tagline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.9, ease: "easeOut" }}
          >
            People are the resource for people.
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.1, ease: "easeOut" }}
          >
            <Link
              to="/app#activities"
              onClick={() => {
                // After navigation, scroll to #activities section
                setTimeout(() => {
                  document.getElementById("activities")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 250);
              }}
              className="relative bg-primary text-white px-7 py-4 rounded-full font-bold inline-flex items-center gap-2 hover:bg-primary-600 transition-all duration-200 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-8px_rgba(255,107,71,0.6)] active:scale-[0.98] hero-cta-pulse"
              data-testid="hero-cta-explore"
            >
              Explore activities near me
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a
              href={STORE.ios}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/95 backdrop-blur border border-white text-ink px-6 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
              data-testid="hero-cta-ios"
            >
              <Apple size={18} /> iOS Beta
            </a>
            <button
              type="button"
              onClick={openAndroidBetaModal}
              className="bg-white/95 backdrop-blur border border-white text-ink px-6 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
              data-testid="hero-cta-android"
            >
              <Play size={18} /> Android Beta
            </button>
          </motion.div>
          <motion.div
            className="mt-10 inline-flex items-center justify-center gap-4 text-sm text-white/85 bg-white/[0.07] backdrop-blur-md border border-white/15 px-4 py-2.5 rounded-full"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 2.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-trust-badge"
          >
            <div className="flex -space-x-2">
              {[IMG.meetup1, IMG.meetup2, IMG.pickleball].map((u, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full border-2 border-white bg-cover bg-center"
                  style={{ backgroundImage: `url(${u})` }}
                />
              ))}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-0.5 text-secondary">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <div className="text-xs mt-0.5">4.9/5 from <LiveStat base={2500} min={2} max={5} salt={1} testid="hero-trust-live-count" />+ early members</div>
            </div>
          </motion.div>
        </div>

        {/* Subtle scroll cue — fades in last */}
        <motion.div
          className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-[10px] uppercase tracking-[0.3em] font-semibold flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 3.0, ease: "easeOut" }}
        >
          <span>Scroll</span>
          <span className="w-px h-8 bg-white/60 animate-pulse" />
        </motion.div>
      </section>

      {/* Tutorial card now sits below the hero, full-width container */}
      <section className="relative px-6 md:px-12 -mt-12 md:-mt-16 pb-16 md:pb-20" data-testid="hero-tutorial-section">
        <div className="max-w-4xl mx-auto relative z-10">
          <HeroIntroVideo />
        </div>
      </section>
    </>
  );
}

/* ---------------- HERO INTRO VIDEO ---------------- */
function HeroIntroVideo() {
  const videoRef = React.useRef(null);
  const [muted, setMuted] = React.useState(true);
  const [playing, setPlaying] = React.useState(true);

  // Defensive autoplay — production CDN + browser autoplay policy
  // sometimes ignores the `autoplay` attribute. Many browsers also refuse to
  // play videos that aren't in the viewport at page load (tutorial is below
  // the fold), so we use an IntersectionObserver to kick playback when it
  // scrolls into view.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    };
    // Immediate attempt
    tryPlay();
    // IntersectionObserver — when at least 25% in view, attempt play
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && v.paused) tryPlay();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    const onInteract = () => { tryPlay(); };
    const onVisible = () => {
      if (document.visibilityState === "visible" && v.paused) tryPlay();
    };
    window.addEventListener("click", onInteract, { once: true });
    window.addEventListener("touchstart", onInteract, { once: true });
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      io.disconnect();
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    // On narrow viewports (mobile), tap = fullscreen for legibility
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobile) {
      const fsTarget = v.webkitEnterFullscreen ? v : v;
      try {
        if (v.webkitEnterFullscreen) {
          v.muted = false;
          v.webkitEnterFullscreen();
          return;
        }
        if (fsTarget.requestFullscreen) {
          v.muted = false;
          fsTarget.requestFullscreen();
          return;
        }
      } catch {
        /* fall through to play/pause */
      }
    }
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="relative w-full"
      data-testid="hero-intro-video"
    >
      {/* Decorative accents */}
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-primary/20 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-secondary/30 blur-2xl pointer-events-none" />

      <div className="relative rounded-[28px] overflow-hidden bg-ink ring-1 ring-ink/10 shadow-2xl">
        <div className="relative" style={{ aspectRatio: "16 / 9" }}>
          <video
            ref={videoRef}
            src={IMG.introVideo}
            poster={IMG.introVideoPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            onCanPlay={(e) => {
              // Extra safety: try to play as soon as enough data is buffered
              if (e.currentTarget.paused) {
                e.currentTarget.play().catch(() => {});
              }
            }}
            className="absolute inset-0 w-full h-full object-cover bg-black"
            data-testid="hero-intro-video-element"
          />
          {/* Click-to-play/pause overlay */}
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center group/play focus:outline-none"
            aria-label={playing ? "Pause intro video" : "Play intro video"}
            data-testid="hero-intro-play-toggle"
          >
            {!playing && (
              <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover/play:scale-110 transition-transform">
                <Play size={26} className="text-ink ml-1" fill="currentColor" />
              </span>
            )}
          </button>
          {/* Sound toggle (desktop) — on mobile we replace with an expand hint */}
          <button
            type="button"
            onClick={toggleSound}
            className="hidden md:inline-flex absolute bottom-3 right-3 bg-ink/70 hover:bg-ink backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 rounded-full items-center gap-1.5 transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid="hero-intro-sound-toggle"
          >
            {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            {muted ? "Tap for sound" : "Sound on"}
          </button>
          {/* Mobile hint — tells the user a tap will open the fullscreen video */}
          <div className="md:hidden absolute bottom-3 right-3 bg-ink/70 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 rounded-full inline-flex items-center gap-1.5">
            <Play size={12} fill="currentColor" />
            Tap to watch
          </div>
          {/* Label tag */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-ink text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot" />
            See how it works
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------------- COMMUNITY BANNER (below hero) ---------------- */
function CommunityBanner() {
  return (
    <section className="relative bg-ink" data-testid="community-banner">
      <div className="relative w-full overflow-hidden" style={{ maxHeight: "75vh" }}>
        {/* Photo at native ratio so no heads get cropped */}
        <img
          src={IMG.communityCircle}
          alt="A circle of diverse friends, faces toward the center — the Fun On The Run community"
          className="block w-full h-auto"
          loading="lazy"
          data-testid="community-banner-image"
        />
        {/* Soft fade at the very bottom so it bleeds into the next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
      </div>

      {/* Caption strip below the photo (not over it) so we never cover faces */}
      <div className="bg-ink text-white px-6 md:px-12 py-8 md:py-10 -mt-px">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-secondary mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary pulse-dot" />
              This is the room
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-3xl md:text-5xl">
              Real people. <span className="text-secondary">Real laughter.</span> Real connection.
            </h2>
          </div>
          <p className="text-white/75 text-base md:text-lg max-w-md leading-snug">
            FOTR is where strangers become friends — face to face, plus one at a time.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- ANIMATED BRAND BANNER ---------------- */
export function AnimatedBrandBanner() {
  return (
    <section className="py-10 md:py-14 px-6 md:px-12 bg-background border-y border-line" data-testid="animated-brand-banner">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
        <img
          src={IMG.logoAnimated}
          alt="Fun On The Run Plus One — animated logo"
          width={1500}
          height={899}
          className="w-full max-w-[280px] md:max-w-[320px] h-auto block flex-shrink-0"
          data-testid="animated-logo-banner"
        />
        <div className="flex-1 text-center md:text-left">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">Always moving</div>
          <h2 className="font-display font-black tracking-tighter text-2xl md:text-4xl leading-[0.95]">
            We&apos;re on the run. <br className="hidden md:block" /><span className="text-primary">Come find your plus one.</span>
          </h2>
        </div>
      </div>
    </section>
  );
}

/* ---------------- LIVE TICKER ---------------- */
export function LiveTicker() {
  const items = [
    "6 people planning brunch in Bishop Arts",
    "3 people heading to trivia night in the Marigny",
    "8 people looking for hiking partners at Cedar Ridge",
    "12 people meeting up for live jazz on Frenchmen St",
    "5 pickleball players need a 4th at Reverchon",
    "9 people joining a bayou kayak meetup",
    "4 new members joined Deep Ellum Vinyl Club",
    "7 people forming a French Quarter photo walk",
  ];
  return (
    <div className="border-y border-ink/10 bg-surface-alt py-5 overflow-hidden" data-testid="live-ticker">
      <Marquee gradient={false} speed={42} pauseOnHover>
        {items.map((t, i) => (
          <div key={i} className="mx-8 flex items-center gap-3 font-display text-xl md:text-2xl font-black text-ink">
            <span className="w-2 h-2 rounded-full bg-primary pulse-dot" />
            <span>{t}</span>
            <span className="text-primary ml-8">/</span>
          </div>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------------- MAVERICKS PARTNERSHIP ---------------- */
export function Mavericks() {
  return (
    <section className="py-16 md:py-24 px-6 md:px-12 bg-ink text-white relative overflow-hidden" data-testid="mavericks-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-white/10 text-white px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
            Official community partner
          </div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            Now partnered with the <span className="text-secondary">Dallas Mavericks</span>.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-md">
            We&apos;ve teamed up with the Mavs on a citywide community initiative — building stronger neighborhoods, creating opportunities, and making a lasting impact. One kid, one play, one person at a time.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 max-w-md">
            <div>
              <div className="font-display font-black text-2xl">Community</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Stronger together</div>
            </div>
            <div>
              <div className="font-display font-black text-2xl">Opportunity</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">For everyone</div>
            </div>
            <div>
              <div className="font-display font-black text-2xl">Impact</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">That lasts</div>
            </div>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7">
          <div className="rounded-[1.25rem] p-1.5 bg-[#FF6C5B] shadow-2xl">
            <div className="group relative rounded-2xl overflow-hidden">
              <img
                src={IMG.mavericks}
                alt="Dallas Mavericks and Fun On The Run Plus One community partnership"
                className="w-full h-auto block transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                data-testid="mavericks-image"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- CAMPUS PARTNERS (TCU + UTD) ---------------- */
export function CampusPartners() {
  return (
    <section className="py-20 md:py-28 px-6 md:px-12 bg-ink text-white relative overflow-hidden border-t border-white/5" data-testid="campus-partners-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-white/10 text-white px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
            Campus partner spotlight
          </div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            FOTR on the <span className="text-secondary">TCU</span> campus.
          </h2>
          <p className="mt-5 text-lg text-white/70 max-w-2xl">
            Our campus partnership with <span className="text-white font-semibold">TCU in Fort Worth</span> is led by <span className="text-white font-semibold">Cedric James</span> — former NFL wide receiver and TCU&apos;s Director of Athletics — connecting Horned Frog students with real community before, during, and after game day. Plus a parallel campus pilot underway at <span className="text-white font-semibold">UT Dallas</span>.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Spotlight photo */}
          <motion.div {...fadeUp} className="lg:col-span-5 rounded-[1.25rem] p-1.5 bg-[#44CF76] shadow-2xl">
            <div className="group relative rounded-2xl overflow-hidden min-h-[464px]" data-testid="tcu-photo-card">
              <div
                className="absolute inset-0 bg-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]"
                style={{ backgroundImage: `url(${IMG.tcuMascot})`, backgroundPosition: "center 15%" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary mb-2">From the field · TCU innovation event</div>
                <div className="font-display font-black text-2xl md:text-3xl leading-tight text-white">Meeting SuperFrog at TCU.</div>
                <div className="text-white/70 text-sm mt-2">FOTR presenting on campus — community, connection, and Horned Frog pride.</div>
              </div>
            </div>
          </motion.div>

          {/* Videos */}
          <motion.div {...fadeUp} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
            <PartnerVideoCard
              src={IMG.tcuVideo}
              poster={`${process.env.REACT_APP_BACKEND_URL}/api/media/tcu_cedric_poster.jpg?v=2`}
              title="Cedric James on FOTR × TCU"
              tag="Director of Athletics · TCU"
              testid="tcu-video-cedric"
              frame="bg-[#4C57FA]"
            />
            <PartnerVideoCard
              src={IMG.tcuPartnerVideo}
              poster={`${process.env.REACT_APP_BACKEND_URL}/api/media/tcu_partner_poster.jpg`}
              title="TCU partnership in action"
              tag="On campus · live moment"
              testid="tcu-video-partner"
              frame="bg-[#BCECC9]"
            />
          </motion.div>
        </div>

        {/* UT Dallas spotlight — parallel to TCU above */}
        <motion.div {...fadeUp} className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0" data-testid="campus-utd-strip">
          <div className="lg:col-span-7 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary">Also live</span>
              <span className="font-display font-black text-3xl md:text-4xl leading-none">UT Dallas</span>
            </div>
            <p className="text-white/75 text-base leading-snug max-w-lg">
              Campus pilot live with <span className="text-white font-semibold">UTD</span> in Dallas — building community programming for Comets across north campus.
            </p>
            <div className="mt-6">
              <a href="/join" className="inline-flex items-center gap-2 bg-white text-ink px-5 py-3 rounded-full font-semibold text-sm hover:bg-white/90 transition-colors" data-testid="campus-utd-cta">
                Bring FOTR to your campus <ArrowRight size={16} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 p-4 md:p-5 lg:pl-0">
            <PartnerVideoCard
              src={IMG.utdVideo}
              poster={IMG.utdVideoPoster}
              title="UTD partnership"
              tag="On campus · UT Dallas"
              testid="utd-video-partner"
              frame="bg-[#FFB768]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PartnerVideoCard({ src, poster, title, tag, testid, frame }) {
  const [playing, setPlaying] = useState(false);
  const framed = frame && !playing;
  return (
    <div className={framed ? `rounded-[1.25rem] p-1.5 shadow-2xl ${frame}` : ""}>
      <div className={`relative aspect-[4/5] ${framed ? "rounded-2xl" : "rounded-3xl ring-1 ring-white/10"} overflow-hidden bg-ink group`} data-testid={testid}>
        {playing ? (
          <video
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
        ) : (
          <>
            {poster && (
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]"
                style={{ backgroundImage: `url(${poster})` }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 group-hover:from-primary/70 transition-colors" />
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 flex items-center justify-center"
              aria-label={`Play ${title}`}
            >
              <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={22} className="text-ink ml-1" fill="currentColor" />
              </div>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">{tag}</div>
              <div className="font-display font-black text-xl md:text-2xl leading-tight text-white">{title}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- VENDORS TEASER (landing page) ---------------- */
export function VendorsTeaser() {
  return (
    <section className="py-20 md:py-28 px-6 md:px-12 bg-surface-alt relative overflow-hidden" data-testid="vendors-teaser">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-ink text-secondary px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
              For vendors
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95] text-ink">
              Got a business? <br />Turn dead inventory into <span className="text-primary">tonight&apos;s line out the door.</span>
            </h2>
            <p className="mt-6 text-lg text-muted max-w-xl">
              <span className="text-ink font-semibold">Starbucks, Panera, Topgolf, Chicken N Pickle</span> — all in conversation with us. Why? Their slow hours + surplus inventory become same-day push alerts. <span className="text-ink font-semibold">Buy-one-get-one, 40% off, sometimes 90% off</span> — and your store fills with FOTR members in minutes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/vendors" className="inline-flex items-center justify-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full font-semibold btn-shimmer hover:bg-primary transition-colors" data-testid="vendors-teaser-cta">
                See how vendors win <ArrowRight size={18} />
              </a>
              <a href="/vendors#vendor-signup" className="inline-flex items-center justify-center gap-2 bg-white text-ink border border-line px-6 py-3.5 rounded-full font-semibold hover:border-ink transition-colors" data-testid="vendors-teaser-cta-2">
                Partner with FOTR
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Connect Now */}
            <div className="rounded-3xl bg-ink text-white p-7 relative overflow-hidden min-h-[260px] flex flex-col" data-testid="vendors-feature-now">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/30 blur-3xl" />
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-primary text-white flex items-center justify-center mb-5">
                  <Zap size={20} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary mb-2">Connect now</div>
                <div className="font-display font-black text-2xl leading-tight">Push alert · fills seats in minutes.</div>
                <div className="mt-3 text-white/70 text-sm">Same-day flash promo to nearby members. Move surplus before close.</div>
              </div>
            </div>
            {/* Connect Later */}
            <div className="rounded-3xl bg-secondary text-ink p-7 relative overflow-hidden min-h-[260px] flex flex-col" data-testid="vendors-feature-later">
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-ink/15 blur-3xl" />
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-ink text-white flex items-center justify-center mb-5">
                  <Calendar size={20} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Connect later</div>
                <div className="font-display font-black text-2xl leading-tight">Pre-sales · revenue locked early.</div>
                <div className="mt-3 text-ink/80 text-sm">Schedule a drop, host a pop-up, build anticipation. Sell tickets before doors open.</div>
              </div>
            </div>
            {/* Mini stat strip */}
            <div className="sm:col-span-2 rounded-3xl border border-line bg-white p-6 flex items-center gap-6 flex-wrap" data-testid="vendors-mini-stats">
              <div className="flex items-center gap-3">
                <Coffee size={20} className="text-primary" />
                <div className="text-sm">
                  <div className="font-bold text-ink leading-none">Recover waste</div>
                  <div className="text-muted text-xs mt-1">Last-hour inventory becomes flash promos</div>
                </div>
              </div>
              <div className="hidden md:block w-px h-10 bg-line" />
              <div className="flex items-center gap-3">
                <Store size={20} className="text-primary" />
                <div className="text-sm">
                  <div className="font-bold text-ink leading-none">Foot traffic on demand</div>
                  <div className="text-muted text-xs mt-1">Push alerts to nearby FOTR members</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- BRAND PILLARS ---------------- */
export function BrandPillars() {
  // Split push-pull: alternating lines slide in from opposite horizontal directions
  const slideFromLeft = {
    initial: { opacity: 0, x: -60 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, amount: 0.4 },
  };
  const slideFromRight = {
    initial: { opacity: 0, x: 60 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, amount: 0.4 },
  };
  return (
    <section className="py-20 md:py-28 px-6 md:px-12 bg-surface-alt" data-testid="brand-pillars">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.92]">
          <motion.span
            className="block"
            {...slideFromLeft}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            No planning.
          </motion.span>
          <motion.span
            className="block"
            {...slideFromRight}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            No ghosting.
          </motion.span>
          <motion.span
            className="block text-primary"
            {...slideFromLeft}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            Just real people, in real-time. IRL.
          </motion.span>
        </h2>
        <motion.p
          className="mt-8 text-xl md:text-2xl text-muted max-w-2xl leading-snug"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          Make real connections that fit your life — not your calendar.
        </motion.p>
      </div>
    </section>
  );
}

/* ---------------- APP SHOWCASE ---------------- */
// Split push-pull entrance — adjacent cards move in OPPOSITE vertical directions
// (Ruben-Stom-inspired). Tied to scroll-into-view via whileInView.
const pushUp = {
  initial: { opacity: 0, y: 80 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
};
const pullDown = {
  initial: { opacity: 0, y: -80 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
};

export function AppShowcase() {
  return (
    <section id="showcase" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">The App</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]" data-testid="showcase-headline">
            Built for the way friendships actually start.
          </h2>
          <p className="mt-6 text-lg text-muted max-w-2xl">
            Push alerts, a live community map, real groups — designed so showing up is the easiest part.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Row 1: left card pushes UP, right card pulls DOWN */}
          <motion.div {...pushUp} className="md:col-span-7 bg-ink text-white rounded-4xl p-8 md:p-12 relative overflow-hidden min-h-[420px] group transition-shadow duration-500 hover:shadow-2xl">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary mb-3">01 / Get started fast</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-tight max-w-md">Welcome to the run.</h3>
            <p className="mt-3 text-white/70 max-w-md">FOTR is on the run, having fun, looking for plus ones. Who wants to join?</p>
            <div className="mt-6 flex justify-center md:mt-0 md:absolute md:-bottom-12 md:-right-8 md:block">
              <img
                src={IMG.appWelcome}
                alt="Welcome screen"
                className="w-[220px] md:w-[280px] h-auto drop-shadow-2xl rotate-6 transition-transform duration-700 ease-out group-hover:scale-[1.04] group-hover:-rotate-1"
                data-testid="showcase-phone-welcome"
              />
            </div>
          </motion.div>

          <motion.div {...pullDown} className="md:col-span-5 bg-surface-alt border border-line rounded-4xl relative overflow-hidden min-h-[520px] flex flex-col group transition-shadow duration-500 hover:shadow-2xl">
            <div className="p-8 md:p-10 relative z-10">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">02 / Find your fun</div>
              <h3 className="font-display font-black text-2xl md:text-3xl leading-tight">See what&apos;s happening, then just show up.</h3>
              <p className="mt-2 text-muted text-sm">Connect now, connect later, or browse all of it.</p>
            </div>
            <div className="flex-1 relative">
              <img
                src={IMG.appLifestyle}
                alt="Find Your Fun screen — Connect Now, Connect Later, View All Activity"
                className="absolute inset-0 w-full h-full object-contain object-bottom transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                data-testid="showcase-phone-findfun"
              />
            </div>
          </motion.div>

          {/* Row 2: left card pulls DOWN, right card pushes UP (alternates the push-pull) */}
          <motion.div {...pullDown} className="md:col-span-5 bg-secondary text-ink rounded-4xl p-8 md:p-12 relative overflow-hidden min-h-[420px] group transition-shadow duration-500 hover:shadow-2xl">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3">03 / Pick your vibe</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-tight max-w-[180px]">Pick your interests.</h3>
            <p className="mt-3 text-ink/70 max-w-[200px] text-sm">Cycling, gaming, painting — we match you with people who show up.</p>
            <div className="mt-6 flex justify-center md:mt-0 md:absolute md:-bottom-10 md:-right-10 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
              <RealPhone src={IMG.appInterests} alt="Select your interests" className="md:rotate-[5deg] md:scale-75" testid="showcase-phone-interests" />
            </div>
          </motion.div>

          <motion.div {...pushUp} className="md:col-span-7 bg-surface-alt rounded-4xl p-8 md:p-12 relative overflow-hidden min-h-[420px] group transition-shadow duration-500 hover:shadow-2xl">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">04 / Create an activity</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-tight max-w-md">Start something. Find your crew.</h3>
            <p className="mt-3 text-muted max-w-md">Pick an activity, set the time, drop a location. Push alerts go out to people nearby who care.</p>
            <div className="mt-6 flex justify-center md:mt-0 md:absolute md:-bottom-8 md:-right-4 transition-transform duration-700 ease-out group-hover:scale-[1.04] group-hover:-rotate-2">
              <RealPhone src={IMG.appCreate} alt="Create activity screen" className="md:-rotate-6 md:scale-90" testid="showcase-phone-create" />
            </div>
          </motion.div>

          {/* Card 5 — full-width activity payoff with push-up entrance + lift on hover */}
          <motion.div
            {...pushUp}
            whileHover={{ y: -8 }}
            className="md:col-span-12 bg-ink text-white rounded-4xl relative overflow-hidden min-h-[520px] grid grid-cols-1 md:grid-cols-12 gap-6 group transition-shadow duration-500 hover:shadow-2xl hover:shadow-primary/20 cursor-default"
            data-testid="showcase-card-festival"
          >
            <div className="md:col-span-7 p-8 md:p-14 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary mb-3">05 / They actually show up</div>
              <h3 className="font-display font-black text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95] tracking-tighter">
                Real activities. <span className="text-secondary">Real seats filling up.</span>
              </h3>
              <p className="mt-5 text-white/70 text-lg max-w-lg">
                Every activity gets its own page — with a real-time chat, participant requests, accepted RSVPs, and one-tap calendar sync. The kind of magic that turns &ldquo;maybe&rdquo; into &ldquo;see you there.&rdquo;
              </p>
              <ul className="mt-6 space-y-2.5 text-white/80">
                {[
                  "Live group conversation per activity",
                  "Participant requests & accepted seats",
                  "Time, location & slot count at a glance",
                  "One tap to add to your calendar",
                ].map((x) => (
                  <li key={x} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span className="text-sm md:text-base">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5 relative flex items-center justify-center p-6 md:p-10">
              <img
                src={IMG.appFestival}
                alt="Festival activity page — 33 slots available, 7 accepted participants, join the conversation"
                className="w-full max-w-[340px] h-auto drop-shadow-2xl rotate-2 transition-transform duration-700 ease-out group-hover:scale-[1.06] group-hover:rotate-0"
                data-testid="showcase-phone-festival"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CITY DENSITY ---------------- */
export function CityDensity() {
  const cities = [
    {
      name: "DFW Metro",
      state: "Dallas · Fort Worth, Texas",
      img: IMG.dallas,
      active: 1_156,
      groups: 68,
      activities: 3_420,
      lead: true,
      badges: [
        { label: "Campus partner", value: "UTD", sub: "Dallas" },
        { label: "Campus partner", value: "TCU", sub: "Fort Worth" },
      ],
    },
    {
      name: "New Orleans",
      state: "Louisiana",
      img: IMG.nola,
      active: 612,
      groups: 32,
      activities: 1_910,
      badges: [],
    },
  ];
  return (
    <section id="cities" className="py-24 md:py-32 px-6 md:px-12 bg-ink text-white relative">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-4">City focus</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]" data-testid="cities-headline">
            We go everywhere, so nobody has to feel <span className="text-secondary">alone</span> anywhere.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            From Dallas to Austin to New Orleans and beyond — wherever you land, there&apos;s an activity and a Plus One waiting. We show up in your city so you never have to show up alone.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cities.map((c) => (
            <motion.div
              key={c.name}
              {...fadeUp}
              className="relative rounded-4xl overflow-hidden min-h-[480px] group cursor-pointer"
              style={{ backgroundImage: `url(${c.img})`, backgroundSize: "cover", backgroundPosition: "center" }}
              data-testid={`city-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/10" />
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                {c.lead && (
                  <span className="self-start text-[10px] uppercase tracking-[0.25em] font-bold bg-primary text-white px-3 py-1.5 rounded-full mb-4" data-testid={`city-badge-flagship-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    Flagship metro
                  </span>
                )}
                <h3 className="font-display font-black text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-none tracking-tighter">{c.name}</h3>
                <div className="text-white/70 mt-1">{c.state}</div>
                {c.badges && c.badges.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2" data-testid={`city-partners-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c.badges.map((b) => (
                      <span key={b.value} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full pl-2.5 pr-3.5 py-1.5">
                        <span className="text-[9px] uppercase tracking-[0.2em] text-white/55 font-bold">{b.label}</span>
                        <span className="font-display font-black text-base leading-none">{b.value}</span>
                        <span className="text-[10px] text-white/55 leading-none">· {b.sub}</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-6 grid grid-cols-3 gap-4 border-t border-white/20 pt-5">
                  <Stat n={c.active} label="active today" />
                  <Stat n={c.groups} label="local groups" />
                  <Stat n={c.activities} label="activities" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Expansion strip */}
        <motion.div {...fadeUp} className="mt-6 rounded-4xl bg-white/[0.04] border border-white/10 p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="city-expansion-strip">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-secondary pulse-dot" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary">Now piloting</span>
          </div>
          <div className="flex-1 text-white/80 text-sm md:text-base">
            <span className="font-semibold text-white">Austin, TX</span> is our newest pilot — and beta sign-ups are open to <span className="font-semibold text-white">anyone in the US</span>. We&apos;ll prioritize cities as density builds.
          </div>
          <Link to="/join" className="self-start md:self-auto inline-flex items-center gap-2 bg-secondary text-ink px-4 py-2.5 rounded-full font-semibold text-sm hover:bg-secondary/90 transition-colors" data-testid="city-expansion-cta">
            Join the beta <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl md:text-4xl"><CountUp end={n} /></div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
    </div>
  );
}

/* ---------------- SOCIAL PROOF ---------------- */
export function SocialProof() {
  const testimonials = [
    { name: "Sophia Taylor", role: "Nonprofit Manager · Dallas", q: "I made more real friends here in 2 months than 2 years of dating apps. The activities just hit different.", img: IMG.meetup1 },
    { name: "Ethan Davis", role: "Nonprofit Director · NOLA", q: "We built an entire volunteer community through Fun On The Run. The push alerts are basically magic.", img: IMG.meetup2 },
    { name: "Jackson Walker", role: "Activist", q: "This app prioritizes people. It feels like a real community, not a metric.", img: IMG.pickleball },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-14 md:gap-6 mb-20" data-testid="social-stats">
          <BigLiveStat base={2500} min={0} max={0} salt={2} label="Activities created" testid="stat-activities" liveTick={false} />
          <BigLiveStat base={3100} min={0} max={0} salt={3} label="Connections made" testid="stat-connections" liveTick={false} />
          <BigLiveStat base={1731} min={0} max={0} salt={1} label="Active members" testid="stat-active-members" withHourlyTicker liveTick={false} />
          <BigLiveStat base={5000} min={0} max={0} salt={4} label="Beta sign-ups" testid="stat-beta-signups" liveTick={false} />
        </motion.div>
        <motion.div {...fadeUp} className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">From our growing community</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            <span className="inline-block mr-2 md:mr-3">In</span>minutes, strangers turned into <span className="text-primary">community</span>.
          </h2>
          <p className="mt-5 text-lg text-muted max-w-2xl">That&apos;s what happens when connection meets purpose.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              {...fadeUp}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className="group bg-surface border border-line rounded-4xl p-8 flex flex-col transition-shadow duration-500 hover:shadow-2xl hover:border-primary/30 cursor-default"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-0.5 text-secondary mb-4 transition-transform duration-500 group-hover:scale-110 origin-left">
                {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
              </div>
              <p className="font-display text-2xl leading-snug tracking-tight font-bold flex-1">&ldquo;{t.q}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${t.img})` }} />
                <div>
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-muted">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Meetup photo wall — real FOTR events, with Ruben-inspired darken+zoom+plus hover */}
        <motion.div {...fadeUp} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { src: IMG.meetup1, alt: "FOTR meetup — top", testid: "meetup-photo-1", bgPos: "bg-top" },
            { src: IMG.meetup2, alt: "FOTR meetup — center", testid: "meetup-photo-2", bgPos: "bg-center" },
          ].map((m) => (
            <div
              key={m.testid}
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
              data-testid={m.testid}
            >
              <div
                className={`absolute inset-0 bg-cover ${m.bgPos} transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]`}
                style={{ backgroundImage: `url(${m.src})` }}
                aria-label={m.alt}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-500" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-ink shadow-2xl scale-90 group-hover:scale-100 transition-transform duration-500">
                  <Sparkles size={22} />
                </span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
function HourlyJoinedTicker() {
  const [count, setCount] = React.useState(() => joinedInLastHour({ min: 2, max: 5, salt: 1 }));
  React.useEffect(() => {
    const id = setInterval(() => {
      setCount(joinedInLastHour({ min: 2, max: 5, salt: 1 }));
    }, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="inline-flex items-center gap-2 bg-white shadow-md border border-ink/10 rounded-full pl-2.5 pr-3 py-1.5 pointer-events-auto"
      data-testid="hourly-joined-ticker"
    >
      <span className="relative flex w-2 h-2">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
      </span>
      <span className="text-[11px] sm:text-xs font-semibold text-ink tabular-nums whitespace-nowrap">
        <span className="text-primary font-bold" data-testid="hourly-joined-count">{count}</span> joined in the last hour
      </span>
    </div>
  );
}

function BigStat({ n, label, suffix = "" }) {
  return (
    <div>
      <div className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-ink">
        <CountUp end={n} suffix={suffix} />
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-muted mt-2">{label}</div>
    </div>
  );
}

function BigLiveStat({ base, min, max, salt, label, suffix = "", testid, withHourlyTicker = false, liveTick = true }) {
  return (
    <div className={withHourlyTicker ? "relative" : ""} data-testid={testid ? `${testid}-wrap` : undefined}>
      {withHourlyTicker && (
        <div className="absolute left-0 right-0 bottom-full mb-2 flex justify-center md:justify-start pointer-events-none">
          <HourlyJoinedTicker />
        </div>
      )}
      <div className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-ink leading-none">
        <LiveStat
          base={base}
          min={min}
          max={max}
          salt={salt}
          suffix={suffix}
          liveTick={liveTick}
          testid={testid || "big-live-stat"}
        />
      </div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted mt-2">
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inline-flex w-full h-full rounded-full bg-secondary opacity-70 animate-ping" />
          <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-secondary" />
        </span>
        {label}
      </div>
    </div>
  );
}

/* ---------------- AI CONCIERGE ---------------- */
export function ConciergeSection() {
  return (
    <section id="concierge" className="py-24 md:py-32 px-6 md:px-12 bg-surface-alt">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">AI Activity Concierge</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]" data-testid="concierge-headline">
            Tell us your vibe. <span className="text-primary">We&apos;ll find your night.</span>
          </h2>
          <p className="mt-6 text-lg text-muted">
            Powered by GPT, your concierge knows the city, your interests, and your crew. Skip the scroll — just ask.
          </p>
          <ul className="mt-8 space-y-3">
            {["Suggests activities by mood", "Surfaces local groups in your city", "Hands you off to real humans nearby"].map((x) => (
              <li key={x} className="flex items-center gap-3 text-base">
                <span className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center"><Sparkles size={14} /></span>
                {x}
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7">
          <ConciergeChat embedded />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- FOUNDER ---------------- */
export function Founder() {
  const [playing, setPlaying] = useState(false);
  return (
    <section id="story" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-4xl overflow-hidden bg-ink" data-testid="founder-video">
            {!playing ? (
              <>
                <img
                  src={IMG.founder}
                  alt="Danny Shantae Bush, Founder & CEO of Fun On The Run Plus One"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent pointer-events-none" />
                <button
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group"
                  data-testid="founder-video-play"
                  aria-label="Play founder video"
                >
                  <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                    <Play size={28} className="text-ink ml-1" fill="currentColor" />
                  </div>
                </button>
                <div className="absolute bottom-6 left-6 right-6 pointer-events-none">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mb-1">Brand Story · 60 sec</div>
                  <div className="font-display font-black text-2xl text-white leading-tight">Watch Danny&apos;s story</div>
                </div>
              </>
            ) : (
              <video
                src="https://customer-assets.emergentagent.com/job_activity-connect-24/artifacts/lu7ghgk4_FOTR%20Brand%20Story%20Cutdown%20%2860sec%29%20V3%20%281%29.mp4"
                controls
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="founder-video-player"
              />
            )}
          </div>
          <div className="absolute -bottom-6 -right-6 bg-primary text-white px-5 py-4 rounded-2xl shadow-xl max-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Founder & CEO</div>
            <div className="font-display font-black text-xl mt-0.5">Danny Shantae Bush</div>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">Meet the founder</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-[0.95]">
            &ldquo;Hurricane Katrina taught me that <span className="text-primary">people are the resource for people</span>.&rdquo;
          </h2>
          <div className="mt-6 space-y-4 text-lg text-muted max-w-2xl">
            <p>
              After losing everything in 2005, Danny rebuilt her life around one belief: real community saves lives. Fun On The Run Plus One is the app she wishes existed back then.
            </p>
            <p>
              With 15+ years in IT, she&apos;s now using predictive AI and geo-mapping to make showing up the easiest part of friendship — for everyone.
            </p>
          </div>
          <a href="/join" className="mt-8 inline-flex items-center gap-2 font-semibold text-ink hover:text-primary transition-colors" data-testid="founder-cta">
            Join the mission <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- AMBASSADORS ---------------- */
export function Ambassadors() {
  const tiers = [
    { icon: Trophy, name: "Fitness Trainers", desc: "Bring your class. Build a tribe." },
    { icon: Users, name: "Pickleball Organizers", desc: "Always 4 ready to play." },
    { icon: Heart, name: "Church & Faith Groups", desc: "Deepen community beyond Sunday." },
    { icon: Sparkles, name: "Young Professionals", desc: "Networking that doesn't feel like networking." },
    { icon: Music2, name: "Event Hosts", desc: "Fill seats with the right crowd." },
  ];
  return (
    <section id="ambassadors" className="py-24 md:py-32 px-6 md:px-12 bg-surface-alt">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">Ambassador program</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]" data-testid="ambassadors-headline">
            Be the spark. Grow a tribe. Get rewarded.
          </h2>
          <p className="mt-6 text-lg text-muted max-w-2xl">
            Local organizers, instructors, and hosts get early premium access, revenue share on paid events, and city-leader badges.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="bg-white rounded-3xl p-6 border border-line hover:-translate-y-1 hover:shadow-xl transition-all"
              data-testid={`ambassador-${i}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-ink text-secondary flex items-center justify-center mb-4">
                <t.icon size={18} />
              </div>
              <div className="font-display font-black text-xl leading-tight">{t.name}</div>
              <div className="text-sm text-muted mt-2">{t.desc}</div>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-10">
          <a
            href="/join"
            className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full font-semibold btn-shimmer hover:bg-primary transition-colors"
            data-testid="ambassador-apply"
          >
            Apply to be an ambassador <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- INVESTORS ---------------- */
export function Investors() {
  const [form, setForm] = useState({ name: "", email: "", firm: "", check_size: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email please!");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/investor/inquiry`, form);
      setDone(true);
      toast.success("Inquiry received — check your inbox for the deck.");
    } catch (e) {
      toast.error("Something glitched — try again or email fotr@funontherun.co directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="investors" className="py-24 md:py-32 px-6 md:px-12 bg-ink text-white relative overflow-hidden" data-testid="investors-section">
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-secondary/10 blur-3xl" />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <motion.div {...fadeUp} className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-4">For investors</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            Bet on the company building <span className="text-primary">real-world</span> community at scale.
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-xl">
            We&apos;re not another dating app or feed. People are the resource for people. Fun On The Run Plus One helps you find your tribe, connect instantly, and experience real life together. Already active across Dallas, Fort Worth, Austin, and New Orleans.
          </p>
          <ul className="mt-8 space-y-3 text-white/80">
            {[
              "2,500+ active members across Dallas, Fort Worth, Austin & New Orleans",
              "Official community partner of the Dallas Mavericks",
              "Campus partnerships with TCU and UT Dallas",
              "Predictive AI + geo-mapping moat",
              "Built by a Katrina survivor with 15+ years in IT",
            ].map((x) => (
              <li key={x} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center"><Sparkles size={12} /></span>
                {x}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div {...fadeUp} className="lg:col-span-6">
          {!done ? (
            <form
              onSubmit={submit}
              className="bg-white/5 backdrop-blur rounded-3xl p-7 md:p-9 border border-white/10 space-y-3"
              data-testid="investor-form"
            >
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-secondary">Request pitch deck</div>
                <div className="font-display font-black text-2xl mt-1">Get the deck. Start the conversation.</div>
                <p className="text-sm text-white/60 mt-1">Replies go directly to our founder.</p>
              </div>
              <InvestorInput label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="investor-name" />
              <InvestorInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="investor-email" />
              <InvestorInput label="Firm / Fund (optional)" value={form.firm} onChange={(v) => setForm({ ...form, firm: v })} testid="investor-firm" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Typical check size</label>
                <select
                  value={form.check_size}
                  onChange={(e) => setForm({ ...form, check_size: e.target.value })}
                  className="mt-1 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-primary"
                  data-testid="investor-check-size"
                >
                  <option className="text-ink" value="">Select…</option>
                  <option className="text-ink">Angel ($5K – $50K)</option>
                  <option className="text-ink">$50K – $250K</option>
                  <option className="text-ink">$250K – $1M</option>
                  <option className="text-ink">$1M+</option>
                  <option className="text-ink">Strategic / partner</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Message (optional)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  placeholder="What caught your eye?"
                  className="mt-1 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary resize-none"
                  data-testid="investor-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-primary text-white px-6 py-4 rounded-2xl font-bold btn-shimmer hover:bg-primary-600 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="investor-submit"
              >
                {loading ? "Sending..." : "Send & request the deck"}
                {!loading && <ArrowRight size={18} />}
              </button>
              <p className="text-[11px] text-white/40 mt-2">By submitting, you agree we may follow up via email. We do not share your info.</p>
            </form>
          ) : (
            <div className="bg-white/5 backdrop-blur rounded-3xl p-10 border border-white/10 text-center" data-testid="investor-success">
              <div className="w-14 h-14 rounded-full bg-secondary text-ink flex items-center justify-center mx-auto mb-4">
                <Sparkles size={22} />
              </div>
              <div className="font-display font-black text-3xl">Got it.</div>
              <div className="text-white/70 mt-3">Danny will be in touch personally — usually within 48 hours. The deck is on its way to your inbox.</div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
function InvestorInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary"
        data-testid={testid}
      />
    </div>
  );
}

/* ---------------- REFERRALS ---------------- */
export function Referrals() {
  const rewards = [
    { icon: Gift, n: "01", title: "Event credits", desc: "Earn credits for premium meetups and partner perks every time a friend joins." },
    { icon: Award, n: "02", title: "Premium access", desc: "Refer 3 friends and unlock priority push alerts + early city expansions." },
    { icon: Trophy, n: "03", title: "City-leader badges", desc: "Recognition badges that unlock real social capital inside your community." },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div {...fadeUp} className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">Referral rewards</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-[0.95]">
            Bring your people. <span className="text-primary">Get the perks.</span>
          </h2>
          <p className="mt-6 text-muted">
            Community apps only work with built-in growth. Every invite makes your local map richer — and rewards you for it.
          </p>
        </motion.div>
        <div className="lg:col-span-8 space-y-4">
          {rewards.map((r, i) => (
            <motion.div
              key={r.title}
              {...fadeUp}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="flex items-start gap-6 bg-surface-alt rounded-3xl p-7 border border-line hover:border-primary transition-colors"
              data-testid={`referral-${i}`}
            >
              <div className="font-display font-black text-3xl text-primary tabular-nums shrink-0">{r.n}</div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <r.icon size={20} className="text-ink" />
                  <div className="font-display font-black text-2xl leading-tight">{r.title}</div>
                </div>
                <p className="text-muted mt-2">{r.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- VIDEO CONTENT ---------------- */
export function VideoContent() {
  const videos = [
    {
      src: "https://customer-assets.emergentagent.com/job_activity-connect-24/artifacts/ajrd4ykk_HOW%20TO%20FIND%20YOUR%20TRIBE%20%2860%20sec%29%20V3%20%281%29.mp4",
      poster: IMG.founderCasual,
      title: "How To Find Your Tribe",
      tag: "Brand film · 60 sec",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_activity-connect-24/artifacts/lu7ghgk4_FOTR%20Brand%20Story%20Cutdown%20%2860sec%29%20V3%20%281%29.mp4",
      poster: IMG.team,
      title: "The FOTR Brand Story",
      tag: "Founder cutdown · 60 sec",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_activity-connect-24/artifacts/67lbi3j3_8ec9868e0ccc4cb89973f308c66fb545.MP4",
      poster: IMG.meetup1,
      title: "Real meetup, real-time",
      tag: "From the field · IRL",
    },
    {
      src: IMG.softLaunchVideo,
      poster: IMG.softLaunchPoster,
      title: "The night FOTR went live",
      tag: "Soft launch · IRL",
    },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-ink text-white">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-4">From the field</div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
              The brand <span className="text-secondary">feels alive</span> because the community is.
            </h2>
          </div>
          <div className="flex items-center gap-4 text-white/70">
            <a href="https://instagram.com/funontherunplus1" target="_blank" rel="noopener noreferrer" aria-label="Instagram @funontherunplus1" data-testid="field-social-instagram" className="hover:text-primary transition-colors"><Instagram size={20} /></a>
            <a href="https://tiktok.com/@funontherunplusone" target="_blank" rel="noopener noreferrer" aria-label="TikTok @funontherunplusone" data-testid="field-social-tiktok" className="hover:text-primary transition-colors"><Music2 size={20} /></a>
            <a href="#" aria-label="YouTube" data-testid="field-social-youtube" className="hover:text-primary transition-colors"><Youtube size={20} /></a>
            <span className="text-sm">Follow @funontherunplus1 · @funontherunplusone</span>
          </div>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {videos.map((v, i) => {
            const frames = ["bg-[#F05692]", "bg-[#FFB768]", "bg-[#44CF76]", "bg-[#4C57FA]"];
            return (
              <motion.div
                key={v.title}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`video-${i}`}
              >
                <VideoCard video={v} frame={frames[i % frames.length]} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function VideoCard({ video, frame }) {
  const [playing, setPlaying] = useState(false);
  const hasVideo = !!video.src;
  const framed = frame && !playing;
  return (
    <div className={framed ? `rounded-[1.25rem] p-1.5 shadow-2xl ${frame}` : ""}>
      <div className={`relative aspect-[9/13] ${framed ? "rounded-2xl" : "rounded-3xl"} overflow-hidden bg-ink group`}>
        {playing && hasVideo ? (
          <video
            src={video.src}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover bg-black"
            data-testid="video-player"
          />
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]"
              style={{ backgroundImage: `url(${video.poster})` }}
            />
            {video.logoOverlay && (
              <img
                src={IMG.logoFull}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute top-3 left-3 right-3 w-auto max-w-[55%]"
                style={{ mixBlendMode: "multiply", opacity: 0.95 }}
                data-testid="video-poster-logo-overlay"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10 group-hover:from-primary/70 transition-colors" />
            <button
              onClick={() => hasVideo && setPlaying(true)}
              disabled={!hasVideo}
              className="absolute inset-0 flex items-center justify-center disabled:cursor-default"
              data-testid={hasVideo ? "video-play-button" : "video-coming-soon"}
              aria-label={hasVideo ? `Play ${video.title}` : "Coming soon"}
            >
              <div className={`w-16 h-16 rounded-full bg-white/95 flex items-center justify-center transition-transform ${hasVideo ? "group-hover:scale-110" : "opacity-70"}`}>
                <Play size={22} className="text-ink ml-1" fill="currentColor" />
              </div>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">{video.tag}</div>
              <div className="font-display font-black text-2xl leading-tight text-white">{video.title}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- SAFETY ---------------- */
export function Safety() {
  const items = [
    { icon: Shield, t: "Verified profiles", d: "Real-name & ID verification keeps the community accountable." },
    { icon: Compass, t: "Privacy controls", d: "Decide exactly who sees what about you — block instantly." },
    { icon: Bell, t: "Live moderation", d: "Report anything off — our team responds in minutes." },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-surface-alt">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">Safety first</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
            Your safety. Our priority.
          </h2>
        </motion.div>

        {/* Featured — Persona background checks */}
        <motion.div
          {...fadeUp}
          className="relative mb-6 overflow-hidden rounded-3xl bg-ink text-white"
          data-testid="persona-featured"
        >
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(255,108,91,0.35), transparent 45%), radial-gradient(circle at 85% 85%, rgba(255,195,0,0.18), transparent 45%)" }} />
          <div className="relative grid lg:grid-cols-12 gap-8 p-8 md:p-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-secondary mb-5">
                <BadgeCheck size={14} /> Verified by Persona
              </div>
              <h3 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl leading-[0.95] mb-5">
                You set the <span className="text-primary">bar</span>.
              </h3>
              <p className="text-lg text-white/80 leading-relaxed max-w-xl">
                Some people want to meet only fully verified members. Others want to open the door wider. FOTR lets you decide. We use <strong className="text-white">Persona</strong> to run real-time ID and background checks, and you pick the verification level that matches your comfort — verified-only, everyone, or anything in between.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="persona-toggle">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-2 rounded-full bg-primary text-white">
                  <BadgeCheck size={12} /> Verified only
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-2 rounded-full bg-white/10 text-white">
                  Verified + Unverified
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-2 rounded-full bg-white/10 text-white">
                  Everyone
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 ml-1">
                  <SlidersHorizontal size={11} /> Change any time
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 lg:pl-6 lg:border-l lg:border-white/10">
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/50 mb-4">What Persona checks</div>
              <ul className="space-y-3 text-sm md:text-base">
                {[
                  "Government-issued ID + live selfie match",
                  "Real-time background screening",
                  "Continuous re-verification — not just at signup",
                  "A green checkmark on every profile that passed",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-secondary/20 text-secondary flex items-center justify-center flex-shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="text-white/85 leading-snug">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/50">
                Verification powered by <strong className="text-white/80">Persona</strong> — the identity platform trusted by 200M+ verified users worldwide.
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((x, i) => (
            <motion.div key={x.t} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.08 }} className="bg-white rounded-3xl p-7 border border-line">
              <div className="w-11 h-11 rounded-2xl bg-primary text-white flex items-center justify-center mb-4"><x.icon size={18} /></div>
              <div className="font-display font-black text-2xl leading-tight">{x.t}</div>
              <p className="text-muted mt-2">{x.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- INCLUSIVE COMMUNITY ---------------- */
export function Inclusive() {
  const photos = [
    { src: IMG.inclusive1, ring: "ring-green-400" },
    { src: IMG.inclusive2, ring: "ring-primary" },
    { src: IMG.inclusive3, ring: "ring-purple-400" },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-background" data-testid="inclusive-section">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-start">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4">Inclusive by design</div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.92]">
              Join the ultimate <br /><span className="text-primary">inclusive</span> community.
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="text-lg text-muted leading-relaxed">
              We invite you to join a community that celebrates your uniqueness — and connect with others who appreciate and value the differences that make us all special.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {photos.map((p, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={`aspect-[3/4] rounded-3xl overflow-hidden ring-4 ${p.ring} shadow-xl`}
              style={{ marginTop: i === 1 ? "3rem" : i === 2 ? "1.5rem" : "0" }}
              data-testid={`inclusive-photo-${i}`}
            >
              <img
                src={p.src}
                alt="Diverse community at Fun On The Run Plus One"
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
export function FAQ() {
  const faqs = [
    { q: "Is the app free?", a: "Yes — joining and using the platform is free. Some premium events and partner perks may have associated costs." },
    {
      q: "Which cities are live right now?",
      a: {
        intro: "Our flagship pilots are running in:",
        bullets: [
          "Dallas, TX",
          "Fort Worth, TX",
          "Austin, TX",
          "New Orleans, LA",
        ],
        outro: "Beta sign-ups are open across the entire US. We just prioritize the cities where local density makes the experience great. Campus partnerships at TCU (Fort Worth) and UT Dallas, plus a community partnership with the Dallas Mavericks.",
      },
    },
    { q: "How does the AI Concierge work?", a: "Tell it your mood, time of day, or interest. It surfaces 3 nearby ideas and the groups behind them." },
    { q: "How do I become an ambassador?", a: "Tap 'Apply to be an ambassador' above. We're looking for active organizers, hosts, and instructors." },
    { q: "How does referral rewards work?", a: "Share your unique link. Every friend who joins unlocks credits, premium access, and badges." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <motion.h2 {...fadeUp} className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.95] mb-12">
          Frequently asked.
        </motion.h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <motion.div key={f.q} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }} className="border border-line rounded-3xl bg-white overflow-hidden" data-testid={`faq-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between p-6 text-left"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-display font-bold text-xl">{f.q}</span>
                <ChevronDown
                  size={22}
                  className={`transition-transform shrink-0 ${open === i ? "rotate-180 text-primary" : ""}`}
                />
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-muted">
                  {typeof f.a === "string" ? (
                    f.a
                  ) : (
                    <>
                      {f.a.intro && <p className="mb-3">{f.a.intro}</p>}
                      <ul className="space-y-1.5 mb-3 list-disc list-inside marker:text-primary">
                        {f.a.bullets.map((b) => <li key={b} className="text-ink/85">{b}</li>)}
                      </ul>
                      {f.a.outro && <p>{f.a.outro}</p>}
                    </>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- LEADERBOARD — TOP PLUS ONE BRINGERS ---------------- */
export function Leaderboard() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTop = async () => {
      try {
        const r = await axios.get(`${API}/referral/leaderboard`);
        if (!cancelled && r.data && Array.isArray(r.data.top)) {
          setItems(r.data.top);
        }
      } catch {
        /* fail soft */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchTop();
    const t = setInterval(fetchTop, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Filler placeholders shown UNTIL real referrers fill the leaderboard.
  // Each real signup pushes one filler off the bottom of the visible top 6.
  const FILLERS = [
    { display_name: "Ariana M.",    city: "Dallas, TX",      referral_count: 4, isFiller: true },
    { display_name: "Marcus J.",    city: "Fort Worth, TX",  referral_count: 3, isFiller: true },
    { display_name: "Priya R.",     city: "Austin, TX",      referral_count: 3, isFiller: true },
    { display_name: "Devon W.",     city: "New Orleans, LA", referral_count: 2, isFiller: true },
    { display_name: "Sophia C.",    city: "Dallas, TX",      referral_count: 2, isFiller: true },
    { display_name: "Jalen H.",     city: "Austin, TX",      referral_count: 1, isFiller: true },
  ];

  // Merge real items first, then fill remaining slots with placeholders so we
  // always show 6 names. As real signups roll in, fillers get pushed off the bottom.
  const realCount = items.length;
  const displayItems = [
    ...items.map((r) => ({ ...r, isFiller: false })),
    ...FILLERS.slice(0, Math.max(0, 6 - realCount)),
  ].slice(0, 6);

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <section className="py-20 md:py-28 px-6 md:px-12 bg-ink text-white relative overflow-hidden" data-testid="leaderboard-section">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-secondary/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-secondary font-black mb-3">
              <Trophy size={14} /> Live · Top Plus One bringers
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.92]">
              The people <span className="text-secondary">connecting people</span> and creating new friendships instantly.
            </h2>
            <p className="mt-4 text-white/65 text-base md:text-lg max-w-xl">
              Every friend they bring banks <span className="text-secondary font-bold">10 credits</span> — usable on premium features at launch. Right now, the run is wide open.
            </p>
          </div>
          <a href="/join" className="self-start md:self-end bg-secondary text-ink font-black px-5 py-3 rounded-full inline-flex items-center gap-2 hover:brightness-110 transition" data-testid="leaderboard-join-cta">
            Get my invite link <ArrowRight size={18} />
          </a>
        </div>

        {!loaded ? (
          <div className="h-40 flex items-center justify-center text-white/40 text-sm">Loading leaderboard…</div>
        ) : (
          <ol className="space-y-2.5" data-testid="leaderboard-list">
            {displayItems.map((row, i) => {
              const isTop3 = i < 3;
              return (
                <li
                  key={`${row.display_name}-${i}`}
                  className={`flex items-center gap-4 px-4 md:px-6 py-4 rounded-2xl border ${
                    isTop3
                      ? "bg-gradient-to-r from-secondary/15 via-white/[0.04] to-transparent border-secondary/30"
                      : "bg-white/[0.03] border-white/10"
                  } ${row.isFiller ? "opacity-70" : ""}`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <div className={`shrink-0 w-12 text-center ${isTop3 ? "" : "text-white/40"}`}>
                    {isTop3 ? (
                      <span className="text-2xl" aria-hidden>{medals[i]}</span>
                    ) : (
                      <span className="font-display font-black text-xl">#{i + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-lg md:text-xl truncate flex items-center gap-2">
                      {row.display_name}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-bold mt-0.5">
                      {row.city}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right pr-4 md:pr-8">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-bold">Friends</div>
                    <div className="font-display font-black text-2xl text-white">{row.referral_count}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-bold">Credits</div>
                    <div className={`font-display font-black text-2xl ${isTop3 ? "text-secondary" : "text-white"}`}>
                      {row.credits_earned}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <p className="mt-8 text-center text-xs text-white/40">
          Names show first name + last initial for privacy. Updates every minute.
        </p>
      </div>
    </section>
  );
}

/* ---------------- MOST WANTED CITIES (signups vote) ---------------- */
export function MostWantedCities() {
  const [data, setData] = useState({ cities: [], flagship: [] });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTop = async () => {
      try {
        const r = await axios.get(`${API}/cities/top?limit=8`);
        if (!cancelled && r.data) setData(r.data);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchTop();
    const t = setInterval(fetchTop, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (loaded && data.cities.length === 0) return null;

  const flagship = data.cities.filter((c) => c.is_flagship);
  const featured = data.cities.filter((c) => !c.is_flagship).slice(0, 5);

  return (
    <section className="py-20 md:py-28 px-6 md:px-12 bg-background" data-testid="most-wanted-cities">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary font-black mb-3">
              <Trophy size={14} /> Live · Most wanted cities
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.92]">
              Where are people <span className="text-primary">running</span> next?
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted max-w-xl">
              Every beta signup is a vote. The cities with the most early members get the next FOTR pilot. The leaderboard updates live.
            </p>
          </div>
          <Link
            to="/join"
            className="self-start md:self-end bg-ink text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 hover:bg-primary transition-colors"
            data-testid="cities-vote-cta"
          >
            Vote for my city <ArrowRight size={18} />
          </Link>
        </div>

        {flagship.length > 0 && (
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-black mb-3">Flagship pilots — already running</div>
            <div className="flex flex-wrap gap-2">
              {flagship.map((c) => (
                <div
                  key={c.name}
                  className="inline-flex items-center gap-2 bg-secondary text-ink font-bold px-4 py-2.5 rounded-full text-sm"
                  data-testid={`flagship-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-ink pulse-dot" />
                  {c.name}
                  <span className="text-xs font-semibold opacity-70">· {c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {featured.length > 0 ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-black mb-3">Most-requested next — by demand</div>
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cities-leaderboard-list">
              {featured.map((c, i) => (
                <li
                  key={c.name}
                  className={`flex items-center gap-4 px-5 py-4 rounded-2xl border ${
                    i === 0
                      ? "bg-gradient-to-br from-primary/10 via-white to-secondary/10 border-primary/40"
                      : "bg-white border-line"
                  }`}
                  data-testid={`top-city-${i}`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-display font-black text-sm ${
                    i === 0 ? "bg-primary text-white" : "bg-ink text-white"
                  }`}>
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-lg truncate">{c.name}</div>
                    <div className="text-xs text-muted">{c.count} vote{c.count === 1 ? "" : "s"}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <div className="rounded-2xl border border-line bg-white px-6 py-10 text-center">
            <div className="font-display font-black text-2xl">Be the first to put your city on the map.</div>
            <p className="mt-2 text-muted">Drop your zip below — the city with the most votes goes live next.</p>
            <Link
              to="/join"
              className="mt-5 inline-flex items-center gap-2 bg-primary text-white px-5 py-3 rounded-full font-bold btn-shimmer hover:bg-primary-600 transition-colors"
            >
              Vote for my city <ArrowRight size={16} />
            </Link>
          </div>
        )}

        <p className="mt-6 text-xs text-muted text-center">Live · refreshes every minute · {data.cities.reduce((s, c) => s + c.count, 0)} total signups counted.</p>
      </div>
    </section>
  );
}

/* ---------------- INVITER BANNER (?ref=CODE) ---------------- */
export function InviterBanner() {
  const [info, setInfo] = useState(null); // { valid, referrer_first_name, total_invited }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const urlCode = getRefFromUrl();
    if (urlCode) persistRef(urlCode);
    const code = urlCode || readStoredRef();
    if (!code) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/referral/${encodeURIComponent(code)}`);
        if (!cancelled && r.data && r.data.valid) setInfo(r.data);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info || !info.valid || dismissed) return null;

  return (
    <div
      className="bg-gradient-to-r from-secondary via-secondary to-amber-300 text-ink px-5 py-2.5 text-sm font-semibold flex items-center justify-center gap-3 flex-wrap"
      data-testid="inviter-banner"
    >
      <span className="inline-flex items-center gap-2">
        <Sparkles size={16} />
        Invited by <span className="font-black">{info.referrer_first_name}</span>
      </span>
      <span className="hidden sm:inline opacity-70">·</span>
      <span className="opacity-80 text-xs sm:text-sm">
        They bank <span className="font-black">10 credits</span> when you join — usable on premium features at launch.
      </span>
      <button
        onClick={() => setDismissed(true)}
        className="ml-2 text-ink/60 hover:text-ink text-xs underline"
        data-testid="inviter-banner-dismiss"
        aria-label="Dismiss"
      >
        dismiss
      </button>
    </div>
  );
}

/* ---------------- FINAL CTA / SIGNUP ---------------- */
export function FinalCTA() {
  const [form, setForm] = useState({ name: "", email: "", city: "Dallas", customCity: "", interests: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [signupResult, setSignupResult] = useState(null); // { referral_code, name, ... }
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ referral_count: 0, credits_earned: 0 });

  const shareUrl = useMemo(
    () => (signupResult ? buildShareUrl(signupResult.referral_code) : ""),
    [signupResult]
  );

  const shareMessage = useMemo(() => {
    const firstName = (signupResult?.name || "").split(" ")[0];
    const intro = firstName ? `${firstName} here —` : "Quick one —";
    return `${intro} just joined Fun On The Run Plus One. It's a community for real friendships built around real activities (pickleball, brunch, trivia, etc.) in DFW, Austin & NOLA. Come find your Plus One: ${shareUrl}`;
  }, [signupResult, shareUrl]);

  // Poll stats every 20s while on the share panel (cheap, optimistic UX)
  useEffect(() => {
    if (!signupResult?.referral_code) return;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await axios.get(
          `${API}/referral/stats/${encodeURIComponent(signupResult.referral_code)}`
        );
        if (!cancelled && r.data) setStats(r.data);
      } catch {
        /* polling failure ok */
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, 20000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [signupResult]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email please!");
      return;
    }
    // If they picked "Other", they must type a city
    const cityToSend = form.city === "Other" ? (form.customCity || "").trim() : form.city;
    if (form.city === "Other" && !cityToSend) {
      toast.error("Please type your city so we know where to bring FOTR next.");
      return;
    }
    setLoading(true);
    try {
      const ref = readStoredRef() || getRefFromUrl();
      const res = await axios.post(`${API}/beta/signup`, {
        name: form.name,
        email: form.email,
        city: cityToSend,
        interests: form.interests,
        referred_by: ref || undefined,
      });
      setSignupResult(res.data);
      setDone(true);
      toast.success("You're on the list. Welcome to the crew.");
    } catch (e) {
      toast.error("Something glitched — try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied. Now go drag your Plus One in.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press the link to share.");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Fun On The Run Plus One",
          text: shareMessage,
          url: shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  return (
    <section id="cta" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-6xl mx-auto bg-ink text-white rounded-[40px] p-10 md:p-16 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-primary/30 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-secondary/20 blur-3xl" />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold mb-4">Join the beta</div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.92]">
              Your next favorite <span className="text-secondary">every day</span> is waiting.
            </h2>
            <p className="mt-5 text-white/70 text-lg max-w-md">
              Get early access — <span className="text-white font-semibold">open across the US</span>. Our flagship pilots are running in <span className="text-white font-semibold">Dallas</span>, <span className="text-white font-semibold">Fort Worth</span>, <span className="text-white font-semibold">Austin</span>, and <span className="text-white font-semibold">New Orleans</span>, but the whole country is already running.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href={STORE.ios} target="_blank" rel="noopener noreferrer" className="bg-white text-ink px-5 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="footer-ios">
                <Apple size={18} /> Download on iOS (TestFlight)
              </a>
              <button type="button" onClick={openAndroidBetaModal} className="bg-white text-ink px-5 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="footer-android">
                <Play size={18} /> Get on Android
              </button>
            </div>
          </div>
          {!done ? (
            <form onSubmit={submit} className="bg-white/5 backdrop-blur rounded-3xl p-7 border border-white/10 space-y-3" data-testid="beta-signup-form">
              <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="signup-name" />
              <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="signup-email" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">City</label>
                <select
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value, customCity: "" })}
                  className="mt-1 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-primary"
                  data-testid="signup-city"
                >
                  <option className="text-ink">Dallas</option>
                  <option className="text-ink">Fort Worth</option>
                  <option className="text-ink">Austin</option>
                  <option className="text-ink">New Orleans</option>
                  <option className="text-ink" value="Other">Other — tell us where</option>
                </select>
                {form.city === "Other" && (
                  <input
                    type="text"
                    autoFocus
                    placeholder="Your city, state — or country if outside the US"
                    value={form.customCity || ""}
                    onChange={(e) => setForm({ ...form, customCity: e.target.value })}
                    className="mt-2 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary"
                    data-testid="signup-custom-city"
                  />
                )}
              </div>
              <Input label="What activities are you into?" value={form.interests} onChange={(v) => setForm({ ...form, interests: v })} testid="signup-interests" />
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-primary text-white px-6 py-4 rounded-2xl font-bold btn-shimmer hover:bg-primary-600 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="signup-submit"
              >
                {loading ? "Adding you..." : "Get on the beta list"}
                {!loading && <ArrowRight size={18} />}
              </button>
            </form>
          ) : (
            <div className="bg-white/5 backdrop-blur rounded-3xl p-7 md:p-8 border border-white/10" data-testid="signup-success">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-secondary text-ink flex items-center justify-center">
                  <Sparkles size={20} />
                </div>
                <div>
                  <div className="font-display font-black text-2xl md:text-3xl leading-tight">You&apos;re in.</div>
                  <div className="text-white/60 text-sm">We&apos;ll ping you the moment your city goes live.</div>
                </div>
              </div>

              {signupResult?.referral_code && (
                <div className="mt-2 bg-gradient-to-br from-secondary/15 via-white/5 to-primary/10 border border-secondary/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 text-secondary text-[10px] uppercase tracking-[0.22em] font-black mb-2">
                    <Coins size={14} /> Bring your Plus One · earn 10 credits each
                  </div>
                  <div className="font-display font-black text-xl md:text-2xl leading-[1.1] mb-2">
                    Stockpile credits for premium features at launch.
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    Every friend that joins through your link banks you <span className="text-secondary font-bold">10 credits</span>. When the app launches, premium features cost <span className="text-secondary font-bold">1 credit per use</span> — today they&apos;re free for beta members, so credits you bank now are pure bonus.
                  </p>

                  <div className="bg-ink/60 border border-white/10 rounded-xl px-3 py-3 flex items-center gap-2 mb-3" data-testid="share-link-container">
                    <span className="text-secondary text-xs uppercase tracking-widest font-bold pl-1 hidden sm:inline">Your link</span>
                    <div className="flex-1 min-w-0 font-mono text-xs sm:text-sm text-white/90 truncate" data-testid="share-link">
                      {shareUrl}
                    </div>
                    <button
                      onClick={handleCopy}
                      className="shrink-0 bg-secondary text-ink font-bold text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5 hover:brightness-110 transition"
                      data-testid="copy-share-link"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleShare}
                      className="bg-white text-ink font-bold text-sm px-4 py-2.5 rounded-xl inline-flex items-center gap-2 hover:bg-white/90 transition"
                      data-testid="share-button"
                    >
                      <Share2 size={15} /> Share with a friend
                    </button>
                    <a
                      href={`sms:?&body=${encodeURIComponent(shareMessage)}`}
                      className="bg-white/10 border border-white/15 text-white font-semibold text-sm px-4 py-2.5 rounded-xl inline-flex items-center gap-2 hover:bg-white/20 transition"
                      data-testid="share-sms"
                    >
                      Text it
                    </a>
                  </div>

                  <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 gap-3" data-testid="referral-stats">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">Friends joined</div>
                      <div className="font-display font-black text-3xl text-white mt-0.5">{stats.referral_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">Credits banked</div>
                      <div className="font-display font-black text-3xl text-secondary mt-0.5">{stats.credits_earned}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white/10 border border-white/15 rounded-2xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary"
        data-testid={testid}
      />
    </div>
  );
}

/* ---------------- FOOTER ---------------- */
export function Footer() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <footer className="bg-ink text-white/85 px-6 md:px-12 pt-16 pb-10 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        {/* Talk to us row */}
        <div
          className="mb-12 rounded-3xl bg-white/[0.04] border border-white/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-8"
          data-testid="footer-talk-row"
        >
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-secondary font-bold mb-2">Talk to us</div>
            <div className="font-display font-black text-white text-2xl md:text-3xl leading-tight">
              Have a question? Want to meet Danny?
            </div>
            <p className="mt-2 text-white/60 text-sm max-w-lg">
              Get an immediate answer from FOTR support, or book a 30-min call with Danny — your call, your schedule.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <SupportChatTrigger
              buttonClassName="inline-flex items-center justify-center gap-2 bg-secondary text-ink px-5 py-3.5 rounded-full font-semibold hover:bg-secondary/90 transition-colors"
              buttonLabel="Chat with support"
              testid="footer-support-button"
            />
            <a
              href="https://calendly.com/dannybush-funontherun"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-ink px-5 py-3.5 rounded-full font-semibold hover:bg-white/90 transition-colors"
              data-testid="footer-calendly-button"
            >
              <Calendar size={18} />
              <span className="text-sm">Schedule with Danny</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="inline-block bg-background rounded-2xl p-4" data-testid="footer-logo">
            <img
              src={IMG.logoFull}
              alt="Fun On The Run Plus One"
              className="h-20 md:h-24 w-auto object-contain"
            />
          </div>
          <p className="mt-4 max-w-md text-sm">
            Building real friendships, real activities, real community — one city at a time.
          </p>
          <div className="mt-6 flex items-center gap-4">
            <a href="https://instagram.com/funontherunplus1" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram"><Instagram size={20} className="hover:text-primary transition-colors" /></a>
            <a href="https://tiktok.com/@funontherunplusone" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok"><Music2 size={20} className="hover:text-primary transition-colors" /></a>
            <a href="#" aria-label="YouTube" data-testid="social-youtube"><Youtube size={20} className="hover:text-primary transition-colors" /></a>
            <a href="#" aria-label="Twitter" data-testid="social-twitter"><Twitter size={20} className="hover:text-primary transition-colors" /></a>
          </div>
        </div>
        <div>
          <div className="text-white font-display font-black mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/app" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-app">The App</Link></li>
            <li><Link to="/app" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-cities">Fun in my City</Link></li>
            <li><Link to="/concierge" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-concierge">AI Concierge</Link></li>
            <li><Link to="/join" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-join">Beta sign-up</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-display font-black mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/story" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-story">Founder story</Link></li>
            <li><Link to="/community" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-community">Community</Link></li>
            <li><Link to="/vendors" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-vendors">For Vendors</Link></li>
            <li><Link to="/invest" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-invest">Investors</Link></li>
            <li><Link to="/safety" className="text-white/80 hover:text-primary transition-colors" data-testid="footer-link-safety">Safety</Link></li>
            <li>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                className="text-white/80 hover:text-primary transition-colors text-left"
                data-testid="footer-link-contact"
              >
                Contact
              </button>
            </li>
          </ul>
        </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/10 text-xs flex flex-wrap items-center justify-between gap-3">
        <div>© {new Date().getFullYear()} Fun On The Run Plus One. All rights reserved.</div>
        <div className="text-white/40">Built with love for community.</div>
      </div>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </footer>
  );
}
